import { Dialog } from '@angular/cdk/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertService, LearningTrailsElementsService, NotificationService } from '@app/core';
import { StatusEnum } from '@app/core/models/status.enum';
import { LearningTrailsSectionService } from '@app/core/services';
import { SubdivionService } from '@app/core/services/subdivisions.service';
import { notNull } from '@app/shared/utils';
import { BehaviorSubject, filter, map, Observable, tap } from 'rxjs';
import { ModalLearningFormElementComponent } from '../../components/modal-learning-form-element/modal-learning-form-element.component';
import { getIcon, getStyleTextIcon } from '../../utils/type-mapper';

@Component({
  selector: 'app-trail',
  templateUrl: './trail.component.html',
  standalone: false,
})
export class ElementComponent implements OnInit {
  public readonly learningTrailsSectionService = inject(LearningTrailsSectionService);
  private readonly _dialog = inject(Dialog);
  private readonly _location = inject(Location);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _alertService = inject(AlertService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _subdivionService = inject(SubdivionService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _learningTrailsElementsService = inject(LearningTrailsElementsService);
  public form: FormGroup;
  public hasEvent = false;
  public curso: any;
  public entityType$ = new BehaviorSubject<number>(0);
  public preview$: Observable<string[]>;
  public hasElements$: Observable<boolean>;
  public icon = getIcon;
  public textIcon = getStyleTextIcon;

  public get breadcrumb(): any {
    const path = this._location.path();

    if (path.includes('trilhas-de-aprendizagem')) {
      return { langTag: 'trilhas.titulo', url: '/trilhas-de-aprendizagem' };
    }
    if (path.includes('universidade-corporativa')) {
      return { langTag: 'trilhas.universidade-corporativa', url: '/universidade-corporativa' };
    }
    if (path.includes('ivca')) {
      return { langTag: 'trilhas.ivca', url: '/ivca' };
    }
  }

  public ngOnInit(): void {
    this.initForm();

    const data = this._activatedRoute.snapshot.data;
    if (data?.course_id) {
      this.form.get('curso_id').setValue(data.course_id);
      if (data?.subdivision_id) {
        this.form.get('subdivisao_id').setValue(data.subdivision_id);
      }
    } else if (data?.courseData) {
      this.form.patchValue(data.courseData?.data);
      this.learningTrailsSectionService.sections$.next(data.courseData?.sessoes);
      this.hasEvent = !!data.courseData?.eventos?.length;
    }
    this.preview$ = this._subdivionService.getSubdivision(this.form.value.curso_id).pipe(map(this.helperPreview));

    this.hasElements$ = this.learningTrailsSectionService.sections$.pipe(
      map((sections: any[]) => sections.some((section) => section?.elementos?.length > 0)),
    );
  }

  private helperPreview = ({ data }): string[] => {
    if (
      this.learningTrailsSectionService.sections$.value.some((section) =>
        section.elementos.some((element) => !!element),
      )
    ) {
      const trilha = (data.trilhas || []).find(({ id }) => this.form.value.id === id);
      return [`${this.breadcrumb.url}/vitrine`, trilha.slug];
    }
    return undefined;
  };

  public onAddSection(): void {
    if (this.learningTrailsSectionService.sections$.value.every((section) => !!section.id)) {
      const data = this.learningTrailsSectionService.sections$.value;
      data.push({ titulo: '', id: null });
      this.learningTrailsSectionService.sections$.next(data);
    }
  }

  public onChangeTitleSection(section: any, titulo: string): void {
    if (!titulo) {
      return;
    }

    const data = {
      titulo: this.clearStr(titulo),
      trilha_id: this.form.value.id,
      id: section.id,
    };

    if (section.id) {
      this.learningTrailsSectionService.update(section.id, data).subscribe((resp) => {
        if (resp.ret === StatusEnum.Active) {
          this._notificationService.success(resp.msg);
          const sections = this.learningTrailsSectionService.sections$.value;
          sections[section.ordem - 1].titulo = data.titulo;
          this.learningTrailsSectionService.sections$.next(sections);
        } else this._notificationService.error(resp.msg);
      });
    } else {
      const ordem = this.learningTrailsSectionService.sections$.value.length;
      this.learningTrailsSectionService.store({ ...data, ordem }).subscribe((resp) => {
        if (resp.ret === StatusEnum.Active) {
          this._notificationService.success(resp.msg);
          const sections = this.learningTrailsSectionService.sections$.value;
          const index = sections.length - 1;
          sections[index] = { ...data };
          sections[index].id = resp['id'];
          sections[index].ordem = ordem;
          sections[index].elementos = [];
          this.learningTrailsSectionService.sections$.next(sections);
        } else this._notificationService.error(resp.msg);
      });
    }
  }

  public onDeleteSection(section: any, index: number): void {
    if (!section.id) {
      const data = this.learningTrailsSectionService.sections$.value;
      data.splice(index, 1);
      this.learningTrailsSectionService.sections$.next(this.sortSections(data));
    } else {
      this.learningTrailsSectionService.destroy(section.id).subscribe((resp) => {
        if (resp.ret === StatusEnum.Active) {
          this._notificationService.success(resp.msg);
          const data = this.learningTrailsSectionService.sections$.value;
          data.splice(index, 1);
          this.learningTrailsSectionService.sections$.next(this.sortSections(data));
        } else this._notificationService.error(resp.msg);
      });
    }
  }

  public onOpenElementModal(section_id: number, order: number, data?: any): void {
    this._dialog
      .open<any>(ModalLearningFormElementComponent, {
        data: {
          section_id,
          order,
          data,
          hasEvent: this.hasEvent,
          trail: this._activatedRoute.snapshot.data.courseData.data,
        },
      })
      .closed.pipe(
        notNull(),
        tap((element) => {
          if (!!element?.id) {
            const sections = this.learningTrailsSectionService.sections$.value;
            const sectionIndex = sections.findIndex((section) => section.id === element.sessao_id);
            if (sections[sectionIndex].elementos.some((elem) => elem.id === element.id)) {
              const elementIndex = sections[sectionIndex].elementos.findIndex((elem) => elem.id === element.id);
              sections[sectionIndex].elementos[elementIndex] = element;
            } else {
              sections[sectionIndex].elementos.push(element);
            }
            this.learningTrailsSectionService.sections$.next(sections);
          }
        }),
      )
      .subscribe();
  }

  public onDeleteElement(element: any, sectionIndex: number): void {
    this._alertService
      .confirm({
        message: !element?.evento_historico_curso?.length
          ? 'trilhas.estudio.trilha.deseja-excluir-elemento'
          : 'trilhas.estudio.trilha.deseja-excluir-elemento-historico',
      })
      .pipe(filter((value) => value))
      .subscribe(() => {
        this.deleteElement(element.id, sectionIndex);
      });
  }

  public dropSection(event: CdkDragDrop<any>) {
    if (event.currentIndex === event.previousIndex) {
      return;
    }

    const sections = this.learningTrailsSectionService.sections$.value;

    moveItemInArray(sections, event.previousIndex, event.currentIndex);
    this.learningTrailsSectionService.sections$.next(sections);

    const payload = this.buildSectionsOrderPayload(sections);

    this.learningTrailsSectionService.updateOrdem(payload).subscribe((resp) => {
      if (resp.ret === StatusEnum.Active) {
        this._notificationService.success(resp.msg);
      } else {
        this._notificationService.error(resp.msg);
        moveItemInArray(sections, event.currentIndex, event.previousIndex);
        this.learningTrailsSectionService.sections$.next(sections);
      }
    });
  }

  public dropElement(event: CdkDragDrop<any>, sectionIndex: number) {
    if (event.currentIndex === event.previousIndex) {
      return;
    }

    const sections = this.learningTrailsSectionService.sections$.value;
    const elements = sections[sectionIndex].elementos;
    moveItemInArray(elements, event.previousIndex, event.currentIndex);
    this.learningTrailsSectionService.sections$.next(sections);

    const payload = this.buildOrdemPayload(elements);
    this._learningTrailsElementsService.updateOrdem(payload).subscribe((resp) => {
      if (resp.ret === StatusEnum.Active) {
        this._notificationService.success(resp.msg);
      } else {
        this._notificationService.error(resp.msg);
        moveItemInArray(elements, event.currentIndex, event.previousIndex);
        this.learningTrailsSectionService.sections$.next(sections);
      }
    });
  }

  public moveDownSection(section: any): void {
    const sections = this.learningTrailsSectionService.sections$.value;

    const index = sections.findIndex((s) => s.id === section.id);
    if (index === -1 || index === sections.length - 1) {
      return;
    }

    moveItemInArray(sections, index, index + 1);
    this.learningTrailsSectionService.sections$.next(sections);

    const payload = this.buildSectionsOrderPayload(sections);

    this.learningTrailsSectionService.updateOrdem(payload).subscribe((resp) => {
      if (resp.ret === StatusEnum.Active) {
        this._notificationService.success(resp.msg);
      } else {
        this._notificationService.error(resp.msg);
        moveItemInArray(sections, index + 1, index);
        this.learningTrailsSectionService.sections$.next(sections);
      }
    });
  }

  public moveUpSection(section: any): void {
    const sections = this.learningTrailsSectionService.sections$.value;

    const index = sections.findIndex((s) => s.id === section.id);
    if (index <= 0) {
      return;
    }

    moveItemInArray(sections, index, index - 1);
    this.learningTrailsSectionService.sections$.next(sections);

    const payload = this.buildSectionsOrderPayload(sections);

    this.learningTrailsSectionService.updateOrdem(payload).subscribe((resp) => {
      if (resp.ret === StatusEnum.Active) {
        this._notificationService.success(resp.msg);
      } else {
        this._notificationService.error(resp.msg);
        moveItemInArray(sections, index - 1, index);
        this.learningTrailsSectionService.sections$.next(sections);
      }
    });
  }

  public moveDownElement(element: any, sectionIndex: number): void {
    const sections = this.learningTrailsSectionService.sections$.value;
    const elements = sections[sectionIndex].elementos;
    const index = elements.findIndex((e) => e.id === element.id);
    if (index === -1 || index === elements.length - 1) {
      return;
    }
    moveItemInArray(elements, index, index + 1);
    this.learningTrailsSectionService.sections$.next(sections);

    const payload = this.buildOrdemPayload(elements);
    this._learningTrailsElementsService.updateOrdem(payload).subscribe((resp) => {
      if (resp.ret === StatusEnum.Active) {
        this._notificationService.success(resp.msg);
      } else {
        this._notificationService.error(resp.msg);
        moveItemInArray(elements, index + 1, index);
        this.learningTrailsSectionService.sections$.next(sections);
      }
    });
  }

  public moveUpElement(element: any, sectionIndex: number): void {
    const sections = this.learningTrailsSectionService.sections$.value;
    const elements = sections[sectionIndex].elementos;
    const index = elements.findIndex((e) => e.id === element.id);
    if (index <= 0) {
      return;
    }
    moveItemInArray(elements, index, index - 1);
    this.learningTrailsSectionService.sections$.next(sections);

    const payload = this.buildOrdemPayload(elements);
    this._learningTrailsElementsService.updateOrdem(payload).subscribe((resp) => {
      if (resp.ret === StatusEnum.Active) {
        this._notificationService.success(resp.msg);
      } else {
        this._notificationService.error(resp.msg);
        moveItemInArray(elements, index - 1, index);
        this.learningTrailsSectionService.sections$.next(sections);
      }
    });
  }

  private deleteElement(id: any, sectionIndex: number): void {
    this._learningTrailsElementsService.destroy(id).subscribe({
      next: (resp) => {
        if (resp.ret === StatusEnum.Active) {
          this._notificationService.success(resp.msg);
          const sections = this.learningTrailsSectionService.sections$.value;
          const elements = sections[sectionIndex].elementos;
          elements.splice(
            elements.findIndex((element: any) => element.id === id),
            1,
          );
          this.sortElements(sections[sectionIndex]);
          this.learningTrailsSectionService.sections$.next(sections);
        } else {
          this._notificationService.error(resp.msg);
        }
      },
      error: (error) => this._notificationService.error(error.error.msg),
    });
  }

  private buildOrdemPayload(elements: any[]) {
    return elements.map((element, index) => ({
      id: element.id,
      ordem: index + 1,
    }));
  }

  private buildSectionsOrderPayload(sections: any[]) {
    return sections.map((section, index) => ({
      id: section.id,
      ordem: index + 1,
    }));
  }

  private sortSections(sections: any): any {
    return sections.map((section: any, index: number) => ({ ...section, ordem: (section.ordem = index + 1) }));
  }

  private sortElements(section: any): any {
    return section.elementos.map((element: any, index: number) => ({ ...element, ordem: (element.ordem = index + 1) }));
  }

  private clearStr(str: string): string {
    if (!!str) {
      str = str.replace(/@/gi, '');
    }
    return str;
  }

  private initForm(): void {
    this.form = this._formBuilder.group({
      id: undefined,
      subdivisao_id: undefined,
      curso_id: undefined,
      titulo: [undefined, [Validators.required, Validators.maxLength(70)]],
      carga_horaria: [undefined, [Validators.maxLength(20)]],
      descricao: [undefined],
      tipo: [undefined, [Validators.required]],
      thumbnail: [undefined, [Validators.required]],
      hash: undefined,
      fixar: 0,
      evento_id: undefined,
      sessao_id: undefined,
      ordem: undefined,
    });
  }
}
